// The general-api endpoints that actually consume the x-unleash-session header.
// `resolveSessionId(req, res)` (stpancras-general-api/services/unleashSession.js)
// is called from EXACTLY two places — lineup.controller and
// plan-benefit.controller#getEntitlements — so these are the only request paths
// that need the header. The legacy /api/plan-benefit/plan route never reads it.
const UNLEASH_SESSION_ENDPOINTS = ['/api/pricing/lineup', '/api/pricing/entitlements']

const hostnameOf = (value, base) => {
  try {
    return new URL(value, base).hostname
  } catch (e) {
    return null
  }
}

/**
 * Decide whether a request should carry the x-unleash-session header.
 *
 * Scoped by ENDPOINT, not by host: only the two general-api paths that read the
 * session id qualify, and only when targeted at one of our own origins (the
 * configured API host or the app origin). Everything else is excluded — so the
 * custom header never trips a third-party CORS preflight (YouTube, Google Fonts,
 * LangGraph) and never leaks to a cross-origin first-party frontend such as
 * audience.ahaslides.com that wouldn't allow it.
 *
 * Path-scoping is region-agnostic: /api/pricing/lineup is the same path on .com,
 * .eu and .nl, so no domain allowlist is needed. Header-absence is safe anyway —
 * general-api falls back to the aha_unleash_session cookie, then mints one.
 *
 * Pure and dependency-injected so it is trivially testable; the interceptor
 * supplies apiBaseUrl + appHref at call time.
 *
 * @param {string | undefined} requestUrl absolute or relative request URL
 *   (vue-resource's HttpOptions.url is optional; undefined fails closed)
 * @param {{ apiBaseUrl?: string, appHref?: string }} [context] both ABSOLUTE URLs
 *   (Config.baseUrl, window.location.href); a relative request URL is resolved
 *   against appHref
 * @returns {boolean}
 */
export const shouldSendUnleashSessionHeader = (requestUrl, { apiBaseUrl, appHref } = {}) => {
  if (!requestUrl) return false

  let target
  try {
    target = new URL(requestUrl, appHref)
  } catch (e) {
    return false
  }

  if (!UNLEASH_SESSION_ENDPOINTS.includes(target.pathname)) return false

  // First-party guard: the consuming endpoints only ever live on our own API
  // host (any region) or app origin. This stops the header from reaching a
  // third party — or a different aha frontend — that happens to share the path.
  return target.hostname === hostnameOf(apiBaseUrl)
    || target.hostname === hostnameOf(appHref)
}
